import { defineStore } from "pinia";
import {
  getSubscriptionInfo,
  getSubscriptionCancelInfo,
  getPackageList,
  getUnlimitedPriceDiff,
  getCompanyEmployeesPoints,
  queryCreditCosts,
  type CompanyEmployeesPointsType,
  getSubscriptionRenewStatus,
  type SubscriptionRenewStatusType,
  type SubscriptionCancelInfoType,
} from "@/api";

type PackageInfo = {
  id: string;
  name: string;
  description: string;
  price: number;
  quota: number;
  billInterval: string;
  startTime?: number | string;
};

export type SubscriptionInfoType = {
  id: string;
  packageId: string;
  currentPayPeriodStart: number;
  currentPayPeriodEnd: number;
  currentPeriodStart: number | string;
  currentPeriodEnd: number | string;
  quota: number;
  remainingQuota: number;
  progress: string;
  startTime: number | string;
  firstPay: boolean;
  packageInfo: PackageInfo;
  allUnexpiredPackageInfo: PackageInfo[];
  remainRewardCredits: number;
  renewFail: boolean;
  renewFailMissingPaymentMethod?: boolean;
  isActive: boolean;
  isOneTimePay: boolean;
};

type FindStatusType = {
  dialog: boolean;
  type?: "email" | "phone" | "kp" | "sync" | "";
};

export type CreditCostType = "email" | "phone" | "kp" | "sync";
export type CreditCostsType = Record<CreditCostType, number>;

const toCreditCost = (value: unknown) => {
  const cost = Number(value);
  return Number.isFinite(cost) && cost > 0 ? cost : 0;
};

export const useSubscriptionStore = defineStore("subscription", {
  state: () => ({
    subscription: null as SubscriptionInfoType | null,
    subscriptionCancelInfo: null as SubscriptionCancelInfoType | null,
    subscriptionCancelInfoLoading:
      null as Promise<SubscriptionCancelInfoType | null> | null,
    subscriptionCancelInfoRequestId: 0,
    findStatus: {} as FindStatusType,
    packageList: [] as any[],
    payUnlimitedInfo: null as any,
    pointsInfo: null as CompanyEmployeesPointsType | null,
    renewStatus: null as SubscriptionRenewStatusType | null,
    creditCosts: {
      email: 0,
      phone: 0,
      kp: 0,
      sync: 0,
    } as CreditCostsType,
    creditCostsLoaded: false,
    creditCostsLoading: null as Promise<boolean> | null,
  }),
  getters: {
    subscriptionInfo: (state) => state.subscription,
    getSubscriptionCancelInfo: (state) => state.subscriptionCancelInfo,
    // 信息查找状态
    getFindStatus: (state) => state.findStatus,
    getPackageList: (state) => state.packageList,
    // 获取升级Unlimited版本费用
    getPayUnlimitedInfo: (state) => state.payUnlimitedInfo,
    // 当前积分信息
    getPointsInfo: (state) => state.pointsInfo,
    getRenewStatus: (state) => state.renewStatus,
    hasRenewFailMissingPaymentMethod: (state) =>
      state.renewStatus?.renewFailMissingPaymentMethod ?? false,
    getCreditCosts: (state) => state.creditCosts,
    getCreditCost: (state) => (type: CreditCostType) =>
      state.creditCosts[type] ?? 0,
  },
  actions: {
    async updateSubscriptionInfo() {
      const [{ data, code }] = await Promise.all([
        getSubscriptionInfo(),
        this.updateRenewStatus(),
      ]);
      if (code === 200) {
        this.subscription = data;
        this.clearSubscriptionCancelInfo();
        this.subscription!.progress =
          (this.subscription!.remainingQuota / this.subscription!.quota) * 100 +
          "%";
        return this.subscription;
      }
      return null;
    },
    clearSubscriptionCancelInfo() {
      this.subscriptionCancelInfo = null;
      this.subscriptionCancelInfoLoading = null;
      this.subscriptionCancelInfoRequestId += 1;
    },
    async updateSubscriptionCancelInfo(force = false) {
      if (!force && this.subscriptionCancelInfo) {
        return this.subscriptionCancelInfo;
      }
      if (!force && this.subscriptionCancelInfoLoading) {
        return this.subscriptionCancelInfoLoading;
      }

      const requestId = this.subscriptionCancelInfoRequestId + 1;
      this.subscriptionCancelInfoRequestId = requestId;
      this.subscriptionCancelInfoLoading = getSubscriptionCancelInfo()
        .then(({ data, code }) => {
          if (
            this.subscriptionCancelInfoRequestId === requestId &&
            code === 200 &&
            data
          ) {
            this.subscriptionCancelInfo = data;
            return data;
          }
          return null;
        })
        .finally(() => {
          if (this.subscriptionCancelInfoRequestId === requestId) {
            this.subscriptionCancelInfoLoading = null;
          }
        });

      return this.subscriptionCancelInfoLoading;
    },
    async updateRenewStatus() {
      try {
        const { data, code } = await getSubscriptionRenewStatus();
        if (code === 200 && data) {
          this.renewStatus = data;
          return data;
        }
      } catch (error) {
        console.error("Failed to get subscription renew status", error);
      }
      return null;
    },
    async getPackageListAction(params: any = {}) {
      const { data, code } = await getPackageList(params);
      if (code === 200) {
        this.packageList = data;
        return data;
      }
    },
    async getPayUnlimitedInfoAction() {
      const { data, code } = await getUnlimitedPriceDiff();
      if (code === 200) {
        this.payUnlimitedInfo = data;
        return data;
      }
    },
    async updatePointsInfo() {
      const { data, code } = await getCompanyEmployeesPoints();
      if (code === 200) {
        this.pointsInfo = data;
        return data;
      }
      return null;
    },
    async updateCreditCosts(force = false) {
      if (this.creditCostsLoaded && !force) return true;
      if (this.creditCostsLoading && !force) return this.creditCostsLoading;

      this.creditCostsLoading = queryCreditCosts()
        .then(({ data, code }) => {
          if (code !== 200 || !data) return false;
          this.creditCosts = {
            ...this.creditCosts,
            email: toCreditCost(data.findEmailCredits),
            phone: toCreditCost(data.findPhoneCredits),
            kp: toCreditCost(data.findKpCredits),
            sync: toCreditCost(data.syncCredits),
          };
          this.creditCostsLoaded = true;
          return true;
        })
        .catch((error) => {
          console.error("Failed to get credit costs", error);
          return false;
        })
        .finally(() => {
          this.creditCostsLoading = null;
        });

      return this.creditCostsLoading;
    },
  },
});
